import { Rocket } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CarregandoLista } from "@/components/carregando";

import { ROTA_IMPULSIONAR } from "../api";
import CartaoCampanha from "./CartaoCampanha";
import DialogoHorario from "./DialogoHorario";
import DialogoPeriodo from "./DialogoPeriodo";
import DialogoValor from "./DialogoValor";
import useCampanhasNucleo from "./useCampanhasNucleo";

const ENCERRADAS_VISIVEIS = 3;

/**
 * Seção "Suas campanhas" da tela Anúncios Meta: o botão Impulsionar e um
 * cartão por campanha criada pelo Núcleo. As de fora (Gerenciador, turbinar do
 * Instagram) continuam na tabela de resultado logo abaixo.
 */
export default function CampanhasNucleo({ conexao, podeGerir, recarregarEm }) {
  const navigate = useNavigate();
  const acoes = useCampanhasNucleo(recarregarEm);
  const [valorDe, setValorDe] = useState(null);
  const [periodoDe, setPeriodoDe] = useState(null);
  const [horarioDe, setHorarioDe] = useState(null);
  const [encerrar, setEncerrar] = useState(null);
  const [todas, setTodas] = useState(false);
  const cartoes = acoes.cartoes || [];
  const vivas = cartoes.filter((c) => c.situacao.codigo !== "encerrada");
  const encerradas = cartoes.filter((c) => c.situacao.codigo === "encerrada");
  const visiveis = [...vivas, ...(todas ? encerradas : encerradas.slice(0, ENCERRADAS_VISIVEIS))];
  const impostoPct = conexao?.imposto_pct;

  const botao = podeGerir && (
    <button type="button" onClick={() => navigate(ROTA_IMPULSIONAR)} data-e2e="botao-impulsionar"
      className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90">
      <Rocket className="w-4 h-4" /> Impulsionar
    </button>
  );

  return (
    <section className="space-y-3" data-e2e="campanhas-nucleo">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-bold">Suas campanhas</h2>
          <p className="text-xs text-muted-foreground">Criadas aqui no Núcleo. Dá para pausar, mudar o valor, o horário e a foto sem abrir o Gerenciador da Meta.</p>
        </div>
        {cartoes.length > 0 && botao}
      </div>

      {acoes.cartoes === null ? (
        // Carregamento com cards: as campanhas em lista, com a foto, piscando.
        <CarregandoLista linhas={3} colunas={2} />
      ) : cartoes.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-6 sm:p-8 text-center space-y-3" data-e2e="campanhas-vazio">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto"><Rocket className="w-6 h-6" /></div>
          <p className="font-semibold">Crie sua primeira campanha em poucos minutos</p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Escolha um produto do cardápio, confira o texto e a prévia, diga quanto investir por semana e publique.
            O anúncio vai para o Instagram e o Facebook, e a aprovação da Meta chega no sino.
          </p>
          {botao || <p className="text-xs text-muted-foreground">Você não tem permissão para criar campanhas. Peça ao dono da loja a permissão Impulsione.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {visiveis.map((c) => (
            <CartaoCampanha key={c.meta_campaign_id} campanha={c} conexao={conexao} podeGerir={podeGerir}
              ocupada={acoes.ocupada === c.meta_campaign_id} acoes={acoes} lojaTemHorario={!!acoes.horario?.tem}
              onValor={setValorDe} onPeriodo={setPeriodoDe} onHorario={setHorarioDe} onEncerrar={setEncerrar} />
          ))}
          {encerradas.length > ENCERRADAS_VISIVEIS && (
            <button type="button" onClick={() => setTodas(!todas)} className="text-sm text-primary hover:underline" data-e2e="campanhas-ver-encerradas">
              {todas ? "Mostrar menos campanhas encerradas" : `Ver as ${encerradas.length} campanhas encerradas`}
            </button>
          )}
        </div>
      )}

      <DialogoValor campanha={valorDe} impostoPct={impostoPct} aoFechar={() => setValorDe(null)}
        aoSalvar={(centavos) => acoes.mudarOrcamento(valorDe.meta_campaign_id, centavos)} />
      <DialogoPeriodo campanha={periodoDe} aoFechar={() => setPeriodoDe(null)}
        aoSalvar={(dados) => acoes.mudarPeriodo(periodoDe.meta_campaign_id, dados)} />
      <DialogoHorario campanha={horarioDe} horario={acoes.horario} aoFechar={() => setHorarioDe(null)}
        aoSalvar={(modo) => acoes.mudarHorario(horarioDe.meta_campaign_id, modo)} />
      <AlertDialog open={!!encerrar} onOpenChange={(aberto) => !aberto && setEncerrar(null)}>
        <AlertDialogContent data-e2e="dialogo-encerrar">
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar esta campanha?</AlertDialogTitle>
            <AlertDialogDescription>
              “{encerrar?.titulo}” para de aparecer e não pode ser retomada (para dar um tempo, use Pausar). O que já foi gasto e os pedidos continuam no relatório.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-e2e="confirmar-encerrar"
              onClick={() => { acoes.encerrar(encerrar.meta_campaign_id); setEncerrar(null); }}>Encerrar campanha</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
